import { z } from "zod";

/**
 * OAuth 2.1 für MCP-Clients, die keinen Header setzen können (MCP-Spec §7).
 * Die Formen folgen den RFCs (snake_case), nicht der Hauskonvention — ein
 * fremder Client spricht sie.
 */

export const oauthScopeSchema = z.enum(["read", "write"]);
export type OAuthScope = z.infer<typeof oauthScopeSchema>;

/** RFC 7591 §2 — nur, was wir auswerten; Unbekanntes wird ignoriert. */
export const oauthRegisterRequestSchema = z.object({
  redirect_uris: z.array(z.string().min(1).max(2000)).min(1).max(10),
  client_name: z.string().trim().min(1).max(100).optional(),
  token_endpoint_auth_method: z.string().optional(),
  grant_types: z.array(z.string()).optional(),
  response_types: z.array(z.string()).optional(),
});
export type OAuthRegisterRequest = z.infer<typeof oauthRegisterRequestSchema>;

export const oauthRegisterResponseSchema = z.object({
  client_id: z.string(),
  client_id_issued_at: z.number().int(),
  client_name: z.string(),
  redirect_uris: z.array(z.string()),
  token_endpoint_auth_method: z.literal("none"),
  grant_types: z.array(z.string()),
  response_types: z.array(z.string()),
});
export type OAuthRegisterResponse = z.infer<typeof oauthRegisterResponseSchema>;

/**
 * Parameter von `GET /oauth/authorize` und die versteckten Felder des
 * Zustimmungsformulars (`POST /oauth/authorize`). Alles optional: Welcher
 * Fehler gemeldet wird und WIE (Seite oder Umleitung), hängt davon ab, wie weit
 * die Prüfung kommt — das entscheidet der Dienst, nicht das Schema.
 */
export const oauthAuthorizeParamsSchema = z.object({
  response_type: z.string().optional(),
  client_id: z.string().optional(),
  redirect_uri: z.string().optional(),
  code_challenge: z.string().optional(),
  code_challenge_method: z.string().optional(),
  scope: z.string().optional(),
  state: z.string().max(1000).optional(),
  resource: z.string().optional(),
});
export type OAuthAuthorizeParams = z.infer<typeof oauthAuthorizeParamsSchema>;

export const oauthConsentFormSchema = oauthAuthorizeParamsSchema.extend({
  csrf: z.string().optional(),
  entscheidung: z.enum(["erlauben", "ablehnen"]).optional(),
});
export type OAuthConsentForm = z.infer<typeof oauthConsentFormSchema>;

/** RFC 6749 §4.1.3 und §6. */
export const oauthTokenRequestSchema = z.discriminatedUnion("grant_type", [
  z.object({
    grant_type: z.literal("authorization_code"),
    code: z.string().min(1).max(200),
    code_verifier: z.string().min(1).max(200),
    client_id: z.string().min(1).max(200),
    redirect_uri: z.string().min(1).max(2000),
    resource: z.string().optional(),
  }),
  z.object({
    grant_type: z.literal("refresh_token"),
    refresh_token: z.string().min(1).max(200),
    client_id: z.string().min(1).max(200),
    scope: z.string().max(100).optional(),
    resource: z.string().optional(),
  }),
]);
export type OAuthTokenRequest = z.infer<typeof oauthTokenRequestSchema>;

export const oauthTokenResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal("Bearer"),
  expires_in: z.number().int(),
  refresh_token: z.string(),
  scope: oauthScopeSchema,
});
export type OAuthTokenResponse = z.infer<typeof oauthTokenResponseSchema>;

export const oauthErrorCodeSchema = z.enum([
  "invalid_request",
  "invalid_client",
  "invalid_grant",
  "unauthorized_client",
  "unsupported_grant_type",
  "unsupported_response_type",
  "invalid_scope",
  "access_denied",
  "server_error",
  "invalid_redirect_uri",
  "invalid_client_metadata",
  // Registrierung gedrosselt (429) oder voll (503) — RFC 6749 §4.1.2.1.
  "temporarily_unavailable",
]);
export type OAuthErrorCode = z.infer<typeof oauthErrorCodeSchema>;

export const oauthErrorSchema = z.object({
  error: oauthErrorCodeSchema,
  error_description: z.string().optional(),
});
export type OAuthError = z.infer<typeof oauthErrorSchema>;

/** RFC 9728. */
export const oauthProtectedResourceMetadataSchema = z.object({
  resource: z.string(),
  authorization_servers: z.array(z.string()),
  scopes_supported: z.array(oauthScopeSchema),
  bearer_methods_supported: z.array(z.literal("header")),
});
export type OAuthProtectedResourceMetadata = z.infer<typeof oauthProtectedResourceMetadataSchema>;

/** RFC 8414. */
export const oauthAuthorizationServerMetadataSchema = z.object({
  issuer: z.string(),
  authorization_endpoint: z.string(),
  token_endpoint: z.string(),
  registration_endpoint: z.string(),
  response_types_supported: z.array(z.literal("code")),
  grant_types_supported: z.array(z.enum(["authorization_code", "refresh_token"])),
  code_challenge_methods_supported: z.array(z.literal("S256")),
  token_endpoint_auth_methods_supported: z.array(z.literal("none")),
  scopes_supported: z.array(oauthScopeSchema),
  authorization_response_iss_parameter_supported: z.boolean(),
});
export type OAuthAuthorizationServerMetadata = z.infer<typeof oauthAuthorizationServerMetadataSchema>;
